import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { loginSchema, type LoginData } from "@/lib/validationSchemas"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/useAuth"
import { toastSuccess, toastError } from "@/lib/toast"
import { Eye, EyeOff, Mail, Lock } from "lucide-react"
import GoogleSSOButton from "./GoogleSSOButton"
import { Separator } from "@/components/ui/separator"
import { BrandLogo } from "@/components/ui/brand-logo"
import { Form } from "@/components/ui/form"
import { FormInputField } from "@/components/ui/form-input-field"
import { FormSubmitButton } from "@/components/ui/form-submit-button"
import { useLanguage } from "@/hooks/useLanguage"

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const { t } = useLanguage()

  const text = t.loginModal || t.login // Assuming it's in login or a new section

  const form = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const onSubmit = async (data: LoginData) => {
    setLoading(true)
    try {
      await login({
        email: data.email,
        password: data.password,
      })
      toastSuccess(text.success)
      onSuccess?.()
      onClose()
    } catch (error: any) {
      // Handle Unauthorized or other API errors
      if (error.status === 401) {
        form.setError("root", {
          message: text.error,
        })
        toastError(text.invalid)
      } else if (error?.code === "email_not_confirmed") {
        form.setError("root", {
          message: text.emailNotConfirmed,
        })
        toastError(text.emailNotConfirmed)
      } else {
        toastError(error.message || text.generalError)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          form.reset()
          onClose()
        }
      }}
    >
      <DialogContent className="overflow-hidden rounded-none border-[1px] border-border bg-card p-0 shadow-2xl sm:max-w-[400px]">
        <div className="border-b-[1px] border-border bg-muted/50 p-8">
          <DialogHeader className="space-y-4">
            <div className="flex justify-center">
              <BrandLogo className="h-16" />
            </div>
            <div className="space-y-1">
              <DialogTitle className="text-center font-display text-3xl uppercase tracking-widest text-foreground">
                {text.welcome}
              </DialogTitle>
            </div>
          </DialogHeader>
        </div>

        <div className="grid gap-6 p-8">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {form.formState.errors.root && (
                <div className="rounded-none border border-destructive/50 bg-destructive/10 p-3 text-center text-xs font-bold uppercase tracking-widest text-destructive">
                  {form.formState.errors.root.message}
                </div>
              )}

              <FormInputField
                control={form.control}
                name="email"
                label={text.email}
                placeholder="tu@correo.com"
                icon={<Mail className="h-4 w-4 text-muted-foreground/50" />}
              />

              <FormInputField
                control={form.control}
                name="password"
                label={text.password}
                type={showPassword ? "text" : "password"}
                icon={<Lock className="h-4 w-4 text-muted-foreground/50" />}
                rightElement={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted-foreground transition-colors hover:text-primary"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                }
              />

              <FormSubmitButton
                loading={loading}
                className="h-14 w-full rounded-none font-display text-xl tracking-widest"
              >
                {text.signIn}
              </FormSubmitButton>
            </form>
          </Form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="bg-foreground/10" />
            </div>
            <div className="relative flex justify-center text-xs font-bold uppercase tracking-widest">
              <span className="bg-card px-4 text-muted-foreground/50">{text.or}</span>
            </div>
          </div>

          <GoogleSSOButton
            onSuccess={() => {
              onSuccess?.()
              onClose()
            }}
          />

          <p className="mt-2 text-center text-xs font-medium text-muted-foreground">
            {text.noAccount}{" "}
            <Button
              variant="link"
              className="px-0 text-xs font-black uppercase tracking-widest text-primary"
              onClick={() => {
                onClose()
                window.location.href = "/buyer-signup"
              }}
            >
              {text.signUp}
            </Button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default LoginModal

